import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MapPinned,
  Activity,
  Warehouse,
  Map,
  Truck,
  DollarSign,
  Shield,
  Edit2,
} from 'lucide-react';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { ZoneCommunesTab } from './components/ZoneCommunesTab';
import { ZoneAssignmentsTab } from './components/ZoneAssignmentsTab';
import { ZoneFormModal } from '../Zones/ZoneFormModal';
import {
  fetchZoneDetail,
  fetchZoneAssignments,
  fetchZoneFormOptions,
} from '../../../../features/zones/zonesThunks';
import { clearZoneDetails } from '../../../../features/zones/zonesSlice';
import { formatZonePrice, getCountryLabel } from '../../../../features/zones/zonesUtils';
import { ZONE_COUNTRIES } from '../../../../features/zones/zonesConstants';
import { selectIsStaff } from '../../../../features/auth/authSelectors';

export const ZoneDetail = () => {
  const { zoneId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { zone, assignments, error } = useSelector((state) => state.zones);
  // Only the internal team works across admins; for anyone else the owner is
  // always themselves.
  const isStaff = useSelector(selectIsStaff);

  const [activeTab, setActiveTab] = useState('general');
  const [isEditOpen, setIsEditOpen] = useState(false);

  useEffect(() => {
    if (zoneId) {
      dispatch(fetchZoneDetail(zoneId));
      dispatch(fetchZoneAssignments(zoneId));
      dispatch(fetchZoneFormOptions());
    }
    return () => {
      dispatch(clearZoneDetails());
    };
  }, [zoneId, dispatch]);

  // The error branch is checked first: a failed load leaves `zone` null, and
  // falling through to the spinner would hang the view forever.
  if (error && !zone) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 text-danger p-6 rounded-2xl border border-red-200 dark:border-red-900">
        <h3 className="font-bold text-lg mb-2">Error al cargar</h3>
        <p>{error}</p>
        <button
          onClick={() => navigate('/shipping-zones')}
          className="mt-4 px-4 py-2 bg-white dark:bg-[#1a1a1a] rounded-lg font-medium shadow-sm hover:opacity-90"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  // Only blanks on the very first load: refetching after an edit keeps the
  // current zone on screen instead of flashing back to a spinner.
  if (!zone) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-ninja-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Always the zone's own total: `communesCount` tracks the coverage tab and
  // shrinks to the number of matches while a search is active.
  const totalCommunes = zone.communes || 0;
  const isMassive = zone.country === ZONE_COUNTRIES.MX;

  const tabs = [
    { id: 'general', label: 'Resumen', icon: Activity },
    { id: 'bodegas', label: `Bodegas (${assignments.length})`, icon: Warehouse },
    { id: 'comunas', label: `Comunas (${totalCommunes.toLocaleString('es-CL')})`, icon: Map },
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Zonas" to="/shipping-zones" current={zone.name} />

      <DetailHero
        media={<MapPinned size={40} strokeWidth={1.5} />}
        title={zone.name}
        badges={
          <span
            className={
              isMassive
                ? 'px-3 py-1 rounded-full text-xs font-bold bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-500/20'
                : 'px-3 py-1 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-500/20'
            }
          >
            {getCountryLabel(zone.country)}
          </span>
        }
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <Truck size={14} /> {zone.courier || 'Sin courier'} &bull; ID interno: {zone.id}
            </span>
            <span className="flex items-center gap-1.5 text-ninja-green bg-ninja-green/10 px-2.5 py-1 rounded-lg font-medium">
              <DollarSign size={14} /> {formatZonePrice(zone.price)}
            </span>
            <span className="flex items-center gap-1.5 text-text-muted font-medium">
              <Map size={14} /> {totalCommunes.toLocaleString('es-CL')} comunas
            </span>
          </>
        }
        actions={
          // Kept as it was: the dark call to action has no equivalent among the
          // `Button` variants, and swapping it for a grey one would be a visual
          // change nobody asked for.
          <button
            onClick={() => setIsEditOpen(true)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-ninja-dark dark:bg-white text-white dark:text-black rounded-xl font-bold hover:opacity-90 transition-opacity shadow-lg"
          >
            <Edit2 size={16} /> Editar Zona
          </button>
        }
      />

      {/* Tabs Navigation */}
      <div className="flex items-center gap-8 border-b border-border/60">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 text-sm font-bold transition-all relative ${
                isActive
                  ? 'text-ninja-green'
                  : 'text-text-muted hover:text-ninja-dark dark:hover:text-white'
              }`}
            >
              <Icon size={16} className={isActive ? 'text-ninja-green' : 'opacity-60'} />
              {tab.label}
              {isActive && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-ninja-green rounded-t-full shadow-[0_-2px_10px_rgba(0,192,136,0.5)]"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in slide-in-from-bottom-2 duration-300">
            <div className="lg:col-span-2 bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
              <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-6 flex items-center gap-2">
                <MapPinned className="text-ninja-green" size={20} /> Datos de la Zona
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                    Courier
                  </span>
                  <p className="font-medium text-ninja-dark dark:text-white flex items-center gap-2">
                    <Truck size={14} className="text-text-muted" /> {zone.courier || '-'}
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                    Precio
                  </span>
                  <p className="font-medium text-ninja-dark dark:text-white">
                    {formatZonePrice(zone.price)}
                  </p>
                </div>
                {isStaff && (
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                      Admin
                    </span>
                    <p className="font-medium text-ninja-dark dark:text-white flex items-center gap-2">
                      <Shield size={14} className="text-text-muted" /> {zone.admin_email || 'Global'}
                    </p>
                  </div>
                )}
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                    Origen de la cobertura
                  </span>
                  <p className="font-medium text-ninja-dark dark:text-white">
                    {isMassive ? 'Carga masiva (Excel)' : 'Selección manual de comunas'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border flex flex-col gap-4">
              <h3 className="text-lg font-bold text-ninja-dark dark:text-white">Cobertura</h3>

              <button
                onClick={() => setActiveTab('comunas')}
                className="text-left p-4 rounded-xl border border-border hover:border-ninja-green hover:bg-ninja-greenLight/50 dark:hover:bg-ninja-green/10 transition-colors"
              >
                <span className="block text-3xl font-black text-ninja-dark dark:text-white">
                  {totalCommunes.toLocaleString('es-CL')}
                </span>
                <span className="text-sm text-text-secondary">comunas cubiertas</span>
              </button>

              <button
                onClick={() => setActiveTab('bodegas')}
                className="text-left p-4 rounded-xl border border-border hover:border-ninja-green hover:bg-ninja-greenLight/50 dark:hover:bg-ninja-green/10 transition-colors"
              >
                <span className="block text-3xl font-black text-ninja-dark dark:text-white">
                  {assignments.length}
                </span>
                <span className="text-sm text-text-secondary">bodegas asignadas</span>
              </button>
            </div>
          </div>
        )}

        {activeTab === 'bodegas' && <ZoneAssignmentsTab zoneId={zoneId} />}

        {activeTab === 'comunas' && (
          <ZoneCommunesTab zoneId={zoneId} countryCode={zone.country} />
        )}
      </div>

      <ZoneFormModal
        isOpen={isEditOpen}
        mode="edit"
        zoneId={zoneId}
        onClose={() => setIsEditOpen(false)}
        onSaved={() => dispatch(fetchZoneDetail(zoneId))}
      />
    </div>
  );
};
